import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  Boxes,
  Layers,
  Tag,
  Award,
  Ruler,
  TrendingUp,
  Sparkles,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de vos cartons à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Ne perdez pas de précieuses heures dans les trajets routiers
            ou à essayer de caler des liasses d&apos;emballages
            poussiéreuses dans un coffre trop étroit. Nous gérons
            l&apos;acheminement global de vos équipements directement à
            votre adresse à {city.name}.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Transport en circuit court",
              body: "Nous refusons l'intervention de transporteurs externes ou de réseaux de messagerie généraux. C'est notre propre société qui charge, transporte et dépose vos fournitures en Île-de-France.",
              Icon: Truck,
            },
            {
              title: "Intégrité matérielle garantie",
              body: "Préservés de l'humidité et des variations climatiques au sein de nos véhicules fermés, vos cartons arrivent chez vous dans un état de rigidité impeccable, prêts à être assemblés et scellés.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Écosystème de protection : 3 catégories cliquables ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
            Écosystème de protection
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos solutions globales pour vos biens
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour qu&apos;aucun objet ne soit le point faible de votre
            déménagement, nous avons développé un catalogue d&apos;outils
            complémentaires conçus pour faire face à toutes les contraintes
            de volume et de poids.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              n: "01",
              tag: "Contenants structurels",
              title: "Pour vos charges et vos volumes",
              body: "Des modules à forte densité pour vos charges lourdes (archives, livres), des volumes standards polyvalents, jusqu'aux penderies verticales rigides équipées de tringles pour transférer vos vêtements sur cintres sans un pli.",
              Icon: Boxes,
              chips: [
                { label: "Carton à livres", to: "/produit/carton-a-livres" },
                {
                  label: "Carton standard renforcé",
                  to: "/produit/carton-standard-renforce",
                },
                {
                  label: "Penderie verticale",
                  to: "/produit/carton-penderie-grand",
                },
              ],
            },
            {
              n: "02",
              tag: "Matrices de calage",
              title: "Pour absorber chaque impact",
              body: "Des rouleaux de film à bulles d'air haute résistance pour envelopper le matériel informatique et la vaisselle, complétés par des couvertures épaisses pour immuniser vos meubles vernis ou laqués contre les frictions.",
              Icon: Layers,
              chips: [
                { label: "Papier bulle 10 m", to: "/produit/papier-bulle-10m" },
                {
                  label: "Couverture de déménagement",
                  to: "/produit/couverture-demenagement",
                },
                {
                  label: "Carton à vaisselle",
                  to: "/produit/carton-vaisselle",
                },
              ],
            },
            {
              n: "03",
              tag: "Systèmes de scellage",
              title: "Pour des fermetures hermétiques",
              body: "Des rubans adhésifs à haut pouvoir de cohésion qui s'ancrent instantanément dans la fibre du carton, associés à des dévidoirs ergonomiques pour une fermeture rapide et hermétique.",
              Icon: Tag,
              chips: [
                { label: "Rouleau d'adhésif", to: "/produit/rouleau-adhesif" },
                {
                  label: "Dévidoir pistolet",
                  to: "/produit/devidoir-pistolet-adhesif",
                },
              ],
            },
          ].map(({ n, tag, title, body, Icon, chips }) => (
            <article
              key={n}
              className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] overflow-hidden flex flex-col"
            >
              {/* Header with big number */}
              <div className="flex items-center justify-between bg-white border-b border-[#cfdbe6] px-5 py-4">
                <div className="flex items-center gap-3">
                  <span className="font-display font-extrabold text-3xl tracking-tight text-[#237cb5] leading-none">
                    {n}
                  </span>
                  <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#FF7A00]">
                    {tag}
                  </div>
                </div>
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
              </div>
              {/* Body */}
              <div className="p-5 md:p-6 flex-1 flex flex-col">
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {chips.map(({ label, to }) => (
                    <Link
                      key={label}
                      to={to}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12.5px] font-bold text-[#0f1d2b] transition-colors"
                      data-testid={`city-issy-chip-${to.split("/").pop()}`}
                    >
                      {label}
                      <ArrowRight className="h-3 w-3" strokeWidth={2.6} />
                    </Link>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Pourquoi retenir : 3 critères en cards horizontales avec icon strip ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
            Standards sans compromis
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi retenir notre matériel&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Choisir nos fournitures, c&apos;est adopter un standard de
            qualité pensé pour éliminer le stress du transport grâce à des
            critères techniques sans compromis.
          </p>
        </div>

        <div className="mt-10 space-y-4">
          {[
            {
              kicker: "Gerbage",
              title: "Un indice de gerbage supérieur",
              body: "Nos cartons bénéficient d'une composition renforcée qui leur permet d'encaisser un empilement vertical maximal dans les camions sans jamais fléchir ni écraser ce qu'ils contiennent.",
              Icon: Award,
              accent: "#237cb5",
            },
            {
              kicker: "Ergonomie",
              title: "L'ergonomie au centre de la conception",
              body: "Nos formats de boîtes respectent les standards logistiques professionnels, garantissant une répartition optimale des charges pour faciliter la manutention et protéger votre santé lors des levages.",
              Icon: Ruler,
              accent: "#FF7A00",
            },
            {
              kicker: "Disponibilité",
              title: "Une disponibilité absolue",
              body: "Nos volumes de stockage nous permettent de répondre immédiatement à vos besoins, éliminant tout risque de rupture ou de retard au milieu de votre rétroplanning de préparation.",
              Icon: TrendingUp,
              accent: "#237cb5",
            },
          ].map(({ kicker, title, body, Icon, accent }) => (
            <article
              key={title}
              className="grid grid-cols-1 md:grid-cols-12 items-stretch bg-white rounded-2xl border border-[#cfdbe6] overflow-hidden"
            >
              {/* Left icon strip */}
              <div
                className="md:col-span-2 flex flex-col items-center justify-center py-6 md:py-0 text-white"
                style={{ backgroundColor: accent }}
              >
                <span className="flex items-center justify-center h-14 w-14 rounded-2xl bg-white/15 text-white">
                  <Icon className="h-7 w-7" strokeWidth={2.4} />
                </span>
                <div className="mt-3 text-[11px] uppercase tracking-[0.18em] font-bold text-white/90">
                  {kicker}
                </div>
              </div>
              {/* Right content */}
              <div className="md:col-span-10 p-6 md:p-7">
                <h3 className="font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-issy-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-issy-cta-packs"
          >
            Voir les kits complets
          </Link>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-issy-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
